import { inject, Injectable } from '@angular/core';
import { AdminBannerApiService } from '../api/services/admin-banner-api.service';
import { CollectionRequestBuilder } from '@web/common';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { map, Observable } from 'rxjs';
import { AdminBannerCollectionFilter } from '../models/admin-banner-collection-filter';
import { AdminBannerCollectionItem } from '../models/admin-banner-collection-item';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { blank, Guid, isNotNil, once } from '@web/utils';
import { AdminBannerDetailsForm } from '../models/admin-banner-details-form';
import { AdminBannerCreateRequest } from '../api/requests/admin-banner-create.request';
import { AdminBannerUpdateRequest } from '../api/requests/admin-banner-update.request';
import { AdminBannerDetails } from '../models/admin-banner-details';
import { BannerLocationType } from '../../shared/enums/banner-location-type';

@Injectable({
  providedIn: 'root',
})
export class AdminBannerDataSourceService {
  private readonly bannerApi = inject(AdminBannerApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);

  public createHeaderByFormData(options: {
    data: AdminBannerDetailsForm;
  }): Observable<void> {
    const { data } = options;
    return this.bannerApi
      .createHeader({
        request: new AdminBannerCreateRequest({
          fileId: data.fileId,
          location: data.location,
          smallFileId: data.smallFileId,
          status: data.status,
          url: data.url,
          visibilityEndDate: data.visibilityEndDate,
          visibilityStartDate: data.visibilityStartDate,
        }),
      })
      .pipe(blank());
  }

  public createByFormDataAndLocation(options: {
    data: AdminBannerDetailsForm;
    location: BannerLocationType;
  }): Observable<void> {
    const { data, location } = options;
    return this.bannerApi
      .createByLocation({
        request: new AdminBannerCreateRequest({
          fileId: data.fileId,
          smallFileId: data.smallFileId,
          status: data.status,
          url: data.url,
          visibilityEndDate: data.visibilityEndDate,
          visibilityStartDate: data.visibilityStartDate,
        }),
        location,
      })
      .pipe(blank());
  }

  public fetchHeaderDetailsById(options: {
    id: Guid;
  }): Observable<AdminBannerDetails> {
    const { id } = options;

    return this.bannerApi.fetchHeaderDetailsById({ id }).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new AdminBannerDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            url: response.url,
            id: response.id,
            location: response.location!,
            smallFile: isNotNil(response.smallFile) ? multimediaFromResponse(response.smallFile) : null,
            redirects: response.redirects,
            file: multimediaFromResponse(response.file),
            status: response.status,
            visibilityEndDate: response.visibilityEndDate,
            visibilityStartDate: response.visibilityStartDate,
          }),
      ),
    );
  }

  public fetchDetailsByIdAndLocation(options: {
    id: Guid;
    location: BannerLocationType;
  }): Observable<AdminBannerDetails> {
    const { id, location } = options;

    return this.bannerApi.fetchDetailsByIdAndLocation({ id, location }).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new AdminBannerDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            url: response.url,
            id: response.id,
            location,
            smallFile: isNotNil(response.smallFile) ? multimediaFromResponse(response.smallFile) : null,
            redirects: response.redirects,
            file: multimediaFromResponse(response.file),
            status: response.status,
            visibilityEndDate: response.visibilityEndDate,
            visibilityStartDate: response.visibilityStartDate,
          }),
      ),
    );
  }

  public updateHeaderByFormData(options: {
    id: Guid;
    data: AdminBannerDetailsForm;
  }): Observable<void> {
    const { id, data } = options;

    return this.bannerApi.updateHeaderById({
      id,
      request: new AdminBannerUpdateRequest({
        fileId: data.fileId,
        location: data.location,
        smallFileId: data.smallFileId,
        status: data.status,
        url: data.url,
        visibilityEndDate: data.visibilityEndDate,
        visibilityStartDate: data.visibilityStartDate,
      }),
    });
  }

  public updateByFormDataAndLocation(options: {
    id: Guid;
    data: AdminBannerDetailsForm;
    location: BannerLocationType;
  }): Observable<void> {
    const { id, data, location } = options;

    return this.bannerApi.updateByIdAndLocation({
      id,
      request: new AdminBannerUpdateRequest({
        fileId: data.fileId,
        smallFileId: data.smallFileId,
        status: data.status,
        url: data.url,
        visibilityEndDate: data.visibilityEndDate,
        visibilityStartDate: data.visibilityStartDate,
      }),
      location,
    });
  }

  public fetchHeaderCollection(options: {
    lazyLoad: TableLazyLoad<AdminBannerCollectionFilter>;
  }): Observable<TableDataPayload<AdminBannerCollectionItem>> {
    const { lazyLoad } = options;

    return this.bannerApi
      .fetchHeaderCollection({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('url').build(),
              )
              .appendText((builder) => builder.equalFor('status').build())
              .appendText((builder) => builder.equalFor('location').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendDate((builder) =>
                builder.rangeFor('visibilityStartDate').build(),
              )
              .appendDate((builder) =>
                builder.rangeFor('visibilityEndDate').build(),
              )
              .buildAsArray(),
          )
          .build(),
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminBannerCollectionItem>(
              response.items.map(
                (item) =>
                  new AdminBannerCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    url: item.url,
                    id: item.id,
                    location: item.location!,
                    file: multimediaFromResponse(item.file),
                    status: item.status,
                    visibilityEndDate: item.visibilityEndDate,
                    visibilityStartDate: item.visibilityStartDate,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchCollectionByLocation(options: {
    lazyLoad: TableLazyLoad<AdminBannerCollectionFilter>;
    location: BannerLocationType;
  }): Observable<TableDataPayload<AdminBannerCollectionItem>> {
    const { lazyLoad, location } = options;

    return this.bannerApi
      .fetchCollectionByLocation({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('url').build(),
              )
              .appendText((builder) => builder.equalFor('status').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendDate((builder) =>
                builder.rangeFor('visibilityStartDate').build(),
              )
              .appendDate((builder) =>
                builder.rangeFor('visibilityEndDate').build(),
              )
              .buildAsArray(),
          )
          .build(),
        location,
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminBannerCollectionItem>(
              response.items.map(
                (item) =>
                  new AdminBannerCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    url: item.url,
                    id: item.id,
                    location,
                    file: multimediaFromResponse(item.file),
                    status: item.status,
                    visibilityEndDate: item.visibilityEndDate,
                    visibilityStartDate: item.visibilityStartDate,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchHeaderDetailsFormById(options: {
    id: Guid;
  }): Observable<AdminBannerDetailsForm> {
    const { id } = options;
    return this.bannerApi.fetchHeaderDetailsById({ id }).pipe(
      map(
        (response) =>
          new AdminBannerDetailsForm({
            fileId: response.file?.id,
            location: response.location!,
            smallFileId: response.smallFile?.id,
            status: response.status,
            url: response.url,
            visibilityEndDate: response.visibilityEndDate,
            visibilityStartDate: response.visibilityStartDate,
          }),
      ),
    );
  }

  public fetchDetailsFormByIdAndLocation(options: {
    id: Guid;
    location: BannerLocationType;
  }): Observable<AdminBannerDetailsForm> {
    const { id, location } = options;
    return this.bannerApi.fetchDetailsByIdAndLocation({ id, location }).pipe(
      map(
        (response) =>
          new AdminBannerDetailsForm({
            fileId: response.file?.id,
            location,
            smallFileId: response.smallFile?.id,
            status: response.status,
            url: response.url,
            visibilityEndDate: response.visibilityEndDate,
            visibilityStartDate: response.visibilityStartDate,
          }),
      ),
    );
  }

  public removeHeaderById(options: { id: Guid }): Observable<void> {
    const { id } = options;
    return this.confirmRemoval(() =>
      this.bannerApi.removeHeaderById({ id }).pipe(once()),
    );
  }

  public removeByIdAndLocation(options: {
    id: Guid;
    location: BannerLocationType;
  }): Observable<void> {
    const { id, location } = options;
    return this.confirmRemoval(() =>
      this.bannerApi.removeByIdAndLocation({ id, location }).pipe(once()),
    );
  }

  private confirmRemoval(callback: () => Observable<void>): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz usunąć wybrany baner? Operacja jest nieodwracalna i spowoduje również usunięcie danych o kliknięciach.',
          )
          .accept(() => callback().subscribe(subscriber))
          .build(),
      );
    });
  }
}
